<template>
  <section class="verify-container">
    <header class="verify-header">
      <h1 class="title">Verify Your Account</h1>

      <p class="subtitle" v-if="user">
        Welcome back, {{ user.firstName }}! Verifying your account…
      </p>

      <p class="subtitle" v-else>
        Welcome to Teamwrkr! Please verify your account below.
      </p>
    </header>

    <div class="verify-content">

      <!-- STEP 1: ENTER EMAIL -->
      <div v-if="needsEmail" class="verify-step">
        <h2 class="step-title">Enter Your Email</h2>
        <p class="step-description">
          Enter your email and we'll send you a secure link to sign in.
        </p>

        <b-field label="Email Address">
          <b-input
            v-model="email"
            type="email"
            placeholder="Enter your email"
          />
        </b-field>

        <b-button :loading="loading" type="is-primary" @click="handleEmailSubmit">
          Send Verification Email
        </b-button>
      </div>

      <!-- STEP 2: EMAIL SENT -->
      <div v-if="emailSent" class="verify-step">
        <h2 class="step-title">Check Your Email</h2>
        <p class="step-description">
          We've sent a verification link to <strong>{{ email }}</strong>.
          Click the link in your email to complete sign-in.
        </p>
      </div>

      <!-- AUTO-VERIFY LOADING STATE -->
      <div v-if="loading && !needsEmail && !emailSent" class="verify-step">
        <h2 class="step-title">Verifying Your Account…</h2>
        <p class="step-description">Please wait while we confirm your identity.</p>
        <b-loading :active="true" :is-full-page="false" />
      </div>

    </div>

    <!-- MODALS -->
    <b-modal v-model="showUserNotFoundModal" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <h2 class="modal-card-title">User Not Found</h2>
        </header>
        <section class="modal-card-body">
          <p>
            That email address is not registered with Teamwrkr.
            Please try again using the email address you registered in the app.
          </p>
        </section>
      </div>
    </b-modal>

    <b-modal v-model="notValidEmailModal" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <h2 class="modal-card-title">Invalid Email</h2>
        </header>
        <section class="modal-card-body">
          <p>Please enter a valid email address.</p>
        </section>
      </div>
    </b-modal>

  </section>
</template>

<script>
import { takePendingTeamLink, teamConfirmationRoute, takePendingAccessLink, accessConfirmationRoute } from '@/utils/pendingTeamLink'
import { emailIsValid } from '../../utils/helpers.js';
import { useUserStore } from '@/stores/userStore';

export default {
  name: 'VerifyUser',

  data() {
    return {
      user: null,
      email: this.$route.query.email || '',
      verificationKey: this.$route.query.key || '',
      loading: false,

      needsEmail: false,
      emailSent: false,

      showUserNotFoundModal: false,
      notValidEmailModal: false,
    };
  },

  mounted() {
    this.handlePageSetup();
  },

  methods: {
    async handlePageSetup() {
      // Already logged in
      if (this.$cookies.isKey('teamwrkr-token')) {
        this.redirectToHome();
        return;
      }

      // Magic link auto-verification
      if (this.email && this.verificationKey) {
        this.loading = true;
        await this.autoVerifyUser();
        return;
      }

      // Default flow → enter email manually
      this.needsEmail = true;
    },

    async handleEmailSubmit() {
      if (!this.email || !emailIsValid(this.email)) {
        this.notValidEmailModal = true;
        return;
      }

      this.loading = true;

      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/validate?sendEmail=true`,
          { email: this.email }
        );

        // Switch to "email sent" UI
        this.needsEmail = false;
        this.emailSent = true;
      } catch (err) {
        if (err.response?.status === 404) {
          this.showUserNotFoundModal = true;
        }
      } finally {
        this.loading = false;
      }
    },

    // Auto-verification via magic link
    async autoVerifyUser() {
      try {
        // Get user record
        const validateRes = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/validate?sendEmail=false`,
          { email: this.email }
        );

        this.user = validateRes.data;

        // Confirm identity
        const confirmRes = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/confirm-identity`,
          {
            id: this.user._id,
            key: this.verificationKey
          }
        );

        this.user = confirmRes.data;

        const userStore = useUserStore();
        userStore.setUser(this.user);

        this.setCookie();
      } catch (err) {
        console.error('Auto verify failed:', err);

        this.loading = false;
        this.needsEmail = true;

        this.$buefy.toast.open({
          message: 'Verification link expired or invalid. Please try again.',
          type: 'is-danger'
        });
      }
    },

    setCookie() {
      this.$cookies.set('teamwrkr-token', this.user.token);
      this.redirectAfterSignIn();
    },

    redirectToHome() {
      this.$router.push('/');
    },

    // After /confirm-identity: finish a team invite link parked by Welcome.vue.
    redirectAfterSignIn() {
      // spec-admin-gated-company-access: an access-request decision link parked while signed out.
      const access = takePendingAccessLink();
      if (access) {
        this.$router.push(accessConfirmationRoute(access));
        return;
      }
      const pending = takePendingTeamLink();
      if (pending) {
        this.$router.push(teamConfirmationRoute(pending));
        return;
      }
      this.redirectToHome();
    }
  }
};
</script>

<style lang="scss" scoped>
.verify-container {
  padding: 2rem;
  background-color: $color-surface-soft;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;

  .verify-header {
    text-align: center;
    margin-bottom: 2rem;

    .title {
      font-size: 2rem;
      font-weight: bold;
      color: $color-text;
    }

    .subtitle {
      font-size: 1.2rem;
      color: $color-text-muted;
    }
  }

  .verify-content {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 2rem;
    width: 100%;
    max-width: 500px;

    .verify-step {
      margin-bottom: 2rem;

      .step-title {
        font-size: 1.5rem;
        font-weight: bold;
        color: $color-text;
        margin-bottom: 1rem;
      }

      .step-description {
        font-size: 1rem;
        color: $color-text-muted;
        margin-bottom: 1.5rem;
      }
    }
  }

  .b-button {
    width: 100%;
    font-weight: bold;
  }
}
</style>
